/* eslint-disable jsdoc/require-jsdoc -- Hook 内部状态动作不作为稳定公共 API 导出。 */
import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import {
  createStayResource,
  createStayResourceMedia,
  createStayResourceSku,
  createStayResourceSupplier,
  deactivateStayResourceSupplier,
  deleteStayResource,
  loadStayResource,
  loadStayResourceGeoCandidates,
  loadStayResourceGeoReviews,
  loadStayResourceHousLevels,
  loadStayResourceLegacyMappings,
  loadStayResourceMedia,
  loadStayResources,
  loadStayResourceSkus,
  loadStayResourceSuppliers,
  loadStayResourceSupplierOptions,
  obsoleteStayResourceMedia,
  reorderStayResourceMedia,
  reviewStayResource,
  reviewStayResourceGeo,
  reviewStayResourceSupplier,
  setStayResourceDisabled,
  setStayResourceSkuDisabled,
  stayResourceMutationErrorMessage,
  updateStayResource,
  updateStayResourceMedia,
  updateStayResourceSku,
  updateStayResourceSupplier,
  uploadStayResourceMediaFile,
  validateStayResourceGeoReview,
  validateStayResourceMediaMutation,
  validateStayResourceReviewMutation,
  validateStayResourceSkuMutation,
  validateStayResourceSpuMutation,
  validateStayResourceSupplierMutation,
  dedupeStayResourceGeoCandidates,
  verifyStayResourceGeo,
  type StayResourceMutationErrors,
} from "../service";
import type {
  StayResourceGeoReviewMutation,
  StayResourceListFilters,
  StayResourceListPage,
  StayResourceListParams,
  StayResourceMediaMutation,
  StayResourceMediaOrderItem,
  StayResourceReviewMutation,
  StayResourceSkuMutation,
  StayResourceSpuItem,
  StayResourceSpuMutation,
  StayResourceSupplierMutation,
  StayResourceSupplierItem,
  StayResourceSupplierReviewMutation,
} from "../types";

const DEFAULT_FILTERS: StayResourceListParams = {
  page: 1,
  pageSize: 20,
  sortField: "updated_at",
  sortDirection: "desc",
};

const listRoot = ["stay-resources"] as const;
const detailRoot = (spuId: string) => ["stay-resource", spuId] as const;
const skuRoot = (spuId: string) => ["stay-resource-skus", spuId] as const;
const mediaRoot = (spuId: string) => ["stay-resource-media", spuId] as const;
const supplierRoot = (spuId: string) => ["stay-resource-suppliers", spuId] as const;
const geoRoot = (spuId: string) => ["stay-resource-geo-candidates", spuId] as const;
const geoReviewsRoot = (spuId: string) => ["stay-resource-geo-reviews", spuId] as const;
const legacyRoot = (spuId: string) => ["stay-resource-legacy-mappings", spuId] as const;
const housLevelRoot = ["stay-resource-hous-levels"] as const;

type MutationRequest = {
  execute: () => Promise<unknown>;
  invalidate: QueryKey[];
};

export type StayResourceMutationResult = {
  ok: boolean;
  errors?: StayResourceMutationErrors;
};

/** 协调酒店资源查询、写入和详情子资源状态。 */
export function useStayResourceManagementRuntime(selectedSpuId?: string) {
  const queryClient = useQueryClient();
  const [draftFilters, setDraftFilters] = useState<StayResourceListFilters>(DEFAULT_FILTERS);
  const [filters, setFilters] = useState<StayResourceListParams>(DEFAULT_FILTERS);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const listQuery = useQuery({
    queryKey: [...listRoot, filters],
    queryFn: () => loadStayResources(filters),
    placeholderData: (previous) => previous,
  });
  const detailQuery = useQuery({
    queryKey: detailRoot(selectedSpuId ?? ""),
    queryFn: () => loadStayResource(selectedSpuId ?? ""),
    enabled: Boolean(selectedSpuId),
  });
  const skuQuery = useQuery({
    queryKey: skuRoot(selectedSpuId ?? ""),
    queryFn: () => loadStayResourceSkus(selectedSpuId ?? ""),
    enabled: Boolean(selectedSpuId),
  });
  const mediaQuery = useQuery({
    queryKey: mediaRoot(selectedSpuId ?? ""),
    queryFn: () => loadStayResourceMedia(selectedSpuId ?? ""),
    enabled: Boolean(selectedSpuId),
  });
  const supplierQuery = useQuery({
    queryKey: supplierRoot(selectedSpuId ?? ""),
    queryFn: () => loadStayResourceSuppliers(selectedSpuId ?? ""),
    enabled: Boolean(selectedSpuId),
  });
  const geoQuery = useQuery({
    queryKey: geoRoot(selectedSpuId ?? ""),
    queryFn: async () => dedupeStayResourceGeoCandidates(
      await loadStayResourceGeoCandidates(selectedSpuId ?? ""),
    ),
    enabled: Boolean(selectedSpuId),
  });
  const geoReviewsQuery = useQuery({
    queryKey: geoReviewsRoot(selectedSpuId ?? ""),
    queryFn: () => loadStayResourceGeoReviews(selectedSpuId ?? ""),
    enabled: Boolean(selectedSpuId),
  });
  const legacyQuery = useQuery({
    queryKey: legacyRoot(selectedSpuId ?? ""),
    queryFn: () => loadStayResourceLegacyMappings(selectedSpuId ?? ""),
    enabled: Boolean(selectedSpuId),
  });
  const housLevelQuery = useQuery({
    queryKey: housLevelRoot,
    queryFn: loadStayResourceHousLevels,
  });
  const mutation = useMutation({
    mutationFn: (request: MutationRequest) => request.execute(),
  });
  const geoMutation = useMutation({
    mutationFn: (request: MutationRequest) => request.execute(),
  });

  useEffect(() => {
    if (listQuery.data && listQuery.data.page !== filters.page) {
      setFilters((current) => ({ ...current, page: listQuery.data?.page ?? current.page }));
    }
  }, [filters.page, listQuery.data]);

  function search() {
    setFilters({ ...DEFAULT_FILTERS, ...draftFilters, page: 1 });
  }

  function reset() {
    setDraftFilters(DEFAULT_FILTERS);
    setFilters(DEFAULT_FILTERS);
  }

  function changePage(page: number, pageSize: number = filters.pageSize) {
    const next = { ...filters, page, pageSize };
    setFilters(next);
    setDraftFilters((current) => ({ ...current, page, pageSize }));
  }

  async function performWith(
    target: typeof mutation,
    request: MutationRequest,
  ): Promise<{ ok: true } | { ok: false; message: string }> {
    setMutationErrorMessage("");
    try {
      await target.mutateAsync(request);
      await Promise.all(request.invalidate.map((queryKey) =>
        queryClient.invalidateQueries({ queryKey })));
      return { ok: true };
    } catch (error) {
      const message = stayResourceMutationErrorMessage(error);
      setMutationErrorMessage(message);
      return { ok: false, message };
    }
  }

  async function perform(request: MutationRequest): Promise<{ ok: true } | { ok: false; message: string }> {
    return performWith(mutation, request);
  }

  async function performOk(request: MutationRequest): Promise<boolean> {
    return (await perform(request)).ok;
  }

  async function performValidated<Payload>(
    payload: Payload,
    validate: (value: Payload) => StayResourceMutationErrors,
    request: MutationRequest,
    target: typeof mutation = mutation,
  ): Promise<StayResourceMutationResult> {
    const errors = validate(payload);
    if (Object.keys(errors).length) return { ok: false, errors };
    const outcome = await performWith(target, request);
    if (!outcome.ok) return { ok: false, errors: { reason: outcome.message } };
    return { ok: true };
  }

  const selectedRoots = selectedSpuId ? [
    listRoot,
    detailRoot(selectedSpuId),
  ] : [listRoot];

  return {
    draftFilters,
    setDraftFilters,
    search,
    reset,
    changePage,
    items: listQuery.data?.items ?? [],
    total: listQuery.data?.total ?? 0,
    page: filters.page,
    pageSize: filters.pageSize,
    loading: listQuery.isLoading,
    fetching: listQuery.isFetching,
    error: listQuery.error,
    detail: detailQuery.data,
    detailLoading: detailQuery.isLoading,
    skus: skuQuery.data ?? [],
    skusLoading: skuQuery.isLoading,
    media: mediaQuery.data ?? [],
    mediaLoading: mediaQuery.isLoading,
    suppliers: supplierQuery.data ?? [],
    suppliersLoading: supplierQuery.isLoading,
    geoCandidates: geoQuery.data ?? [],
    geoCandidatesLoading: geoQuery.isLoading,
    geoCandidatesUnavailable: Boolean(geoQuery.error),
    geoReviews: geoReviewsQuery.data ?? [],
    geoReviewsLoading: geoReviewsQuery.isLoading,
    geoReviewsUnavailable: Boolean(geoReviewsQuery.error),
    legacyMappings: legacyQuery.data ?? [],
    legacyMappingsLoading: legacyQuery.isLoading,
    legacyMappingsUnavailable: Boolean(legacyQuery.error),
    housLevelOptions: housLevelQuery.data ?? [],
    housLevelOptionsLoading: housLevelQuery.isLoading,
    searchSupplierOptions(query: string) {
      return loadStayResourceSupplierOptions(query);
    },
    mutationPending: mutation.isPending,
    geoMutationPending: geoMutation.isPending,
    mutationErrorMessage,
    clearMutationError: () => setMutationErrorMessage(""),
    createSpuAndVerifyGeo(payload: StayResourceSpuMutation) {
      return performValidated(payload, validateStayResourceSpuMutation, {
        execute: () => createStayResource(payload),
        invalidate: [listRoot],
      });
    },
    updateSpu(spuId: string, payload: StayResourceSpuMutation) {
      return performValidated(payload, validateStayResourceSpuMutation, {
        execute: () => updateStayResource(spuId, payload),
        invalidate: [listRoot, detailRoot(spuId)],
      });
    },
    reviewResource(spuId: string, payload: StayResourceReviewMutation) {
      return performValidated(payload, validateStayResourceReviewMutation, {
        execute: () => reviewStayResource(spuId, payload),
        invalidate: [listRoot, detailRoot(spuId)],
      });
    },
    toggleSpu(spuId: string, disabled: boolean, cascadeSkus = false) {
      return performOk({
        execute: () => setStayResourceDisabled(spuId, disabled, cascadeSkus),
        invalidate: [listRoot, detailRoot(spuId)],
      });
    },
    deleteSpu(spuId: string) {
      return performOk({
        execute: () => deleteStayResource(spuId),
        invalidate: [listRoot],
      });
    },
    createSku(spuId: string, payload: StayResourceSkuMutation) {
      return performValidated(payload, validateStayResourceSkuMutation, {
        execute: () => createStayResourceSku(spuId, payload),
        invalidate: [listRoot, skuRoot(spuId), detailRoot(spuId)],
      });
    },
    updateSku(spuId: string, skuId: string, payload: StayResourceSkuMutation) {
      return performValidated(payload, validateStayResourceSkuMutation, {
        execute: () => updateStayResourceSku(spuId, skuId, payload),
        invalidate: [listRoot, skuRoot(spuId), detailRoot(spuId)],
      });
    },
    toggleSku(spuId: string, skuId: string, disabled: boolean) {
      return performOk({
        execute: () => setStayResourceSkuDisabled(spuId, skuId, disabled),
        invalidate: [listRoot, skuRoot(spuId), detailRoot(spuId)],
      });
    },
    createMedia(spuId: string, payload: StayResourceMediaMutation) {
      return performValidated(payload, validateStayResourceMediaMutation, {
        execute: () => createStayResourceMedia(spuId, payload),
        invalidate: [listRoot, mediaRoot(spuId), detailRoot(spuId)],
      });
    },
    updateMedia(spuId: string, mediaId: string, payload: StayResourceMediaMutation) {
      return performValidated(payload, validateStayResourceMediaMutation, {
        execute: () => updateStayResourceMedia(spuId, mediaId, payload),
        invalidate: [listRoot, mediaRoot(spuId), detailRoot(spuId)],
      });
    },
    reorderMedia(spuId: string, items: StayResourceMediaOrderItem[]) {
      return performOk({
        execute: () => reorderStayResourceMedia(spuId, items),
        invalidate: [mediaRoot(spuId)],
      });
    },
    obsoleteMedia(spuId: string, mediaId: string) {
      return performOk({
        execute: () => obsoleteStayResourceMedia(spuId, mediaId),
        invalidate: [listRoot, mediaRoot(spuId), detailRoot(spuId)],
      });
    },
    async uploadMedia(file: File): Promise<string | undefined> {
      setMutationErrorMessage("");
      try {
        return (await uploadStayResourceMediaFile(file)).fileUrl;
      } catch (error) {
        setMutationErrorMessage(stayResourceMutationErrorMessage(error));
        return undefined;
      }
    },
    async verifyGeo(spuId: string) {
      const detailKey = detailRoot(spuId);
      const previousDetail = queryClient.getQueryData<StayResourceSpuItem>(detailKey);
      if (previousDetail) {
        queryClient.setQueryData(detailKey, {
          ...previousDetail,
          geoVerificationStatus: "pending",
          geoStatus: "pending",
        });
      }
      const listKey = [...listRoot, filters] as const;
      const previousList = queryClient.getQueryData<StayResourceListPage>(listKey);
      if (previousList) {
        queryClient.setQueryData(listKey, {
          ...previousList,
          items: previousList.items.map((item) => (
            item.staySpuId === spuId
              ? { ...item, geoVerificationStatus: "pending" as const, geoStatus: "pending" as const }
              : item
          )),
        });
      }
      const ok = await performWith(geoMutation, {
        execute: () => verifyStayResourceGeo(spuId),
        invalidate: [...selectedRoots, geoRoot(spuId), geoReviewsRoot(spuId)],
      });
      if (!ok) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: detailKey }),
          queryClient.invalidateQueries({ queryKey: listRoot }),
        ]);
      }
      return ok;
    },
    reviewGeo(spuId: string, payload: StayResourceGeoReviewMutation) {
      return performValidated(payload, validateStayResourceGeoReview, {
        execute: () => reviewStayResourceGeo(spuId, payload),
        invalidate: [...selectedRoots, geoRoot(spuId), geoReviewsRoot(spuId)],
      }, geoMutation);
    },
    createSupplier(spuId: string, payload: StayResourceSupplierMutation) {
      return performValidated(payload, validateStayResourceSupplierMutation, {
        execute: () => createStayResourceSupplier(spuId, payload),
        invalidate: [listRoot, supplierRoot(spuId), detailRoot(spuId)],
      });
    },
    updateSupplier(spuId: string, capabilityId: string, payload: StayResourceSupplierMutation) {
      return performValidated(payload, validateStayResourceSupplierMutation, {
        execute: () => updateStayResourceSupplier(spuId, capabilityId, payload),
        invalidate: [listRoot, supplierRoot(spuId), detailRoot(spuId)],
      });
    },
    reviewSupplier(
      spuId: string,
      item: StayResourceSupplierItem,
      payload: StayResourceSupplierReviewMutation,
    ) {
      return performOk({
        execute: () => reviewStayResourceSupplier(spuId, item, payload),
        invalidate: [supplierRoot(spuId)],
      });
    },
    deactivateSupplier(spuId: string, item: StayResourceSupplierItem) {
      return performOk({
        execute: () => deactivateStayResourceSupplier(spuId, item),
        invalidate: [listRoot, supplierRoot(spuId), detailRoot(spuId)],
      });
    },
  };
}

export type StayResourceManagementRuntime = ReturnType<typeof useStayResourceManagementRuntime>;
